import { pairingStart } from "../account";
import { forgetGuestComputer } from "../identity";
import { useState } from "react";
import { agentOf, describeAvailability, shortFingerprint, type ComputerRecord } from "@foreman/app-core";
import type { Nav } from "../nav";
import { folderKey } from "../home-view";
import { AgentLogo, BackButton, ChevronIcon, ComputerIcon, MoreIcon, PlusIcon, STATUS, Mascot, Sheet, Spinner, TabBar, useApp, useHubSummaries } from "../ui";

type SheetState = null | { kind: "menu" | "rename" | "remove" | "removeOffline"; id: string };

export function ComputersScreen({ nav }: { nav: Nav }) {
  const { hub, store, toast } = useApp();
  const summaries = useHubSummaries(hub);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [renameVal, setRenameVal] = useState("");
  const [busy, setBusy] = useState(false);
  const waiting = summaries.reduce((n, s) => n + s.needsYou, 0);
  const target = sheet ? store.get(sheet.id) : undefined;

  const removeComputer = async (c: ComputerRecord) => {
    const conn = hub.get(c.id);
    if (conn?.getSnapshot().state === "ready") {
      setBusy(true);
      try {
        await conn.removeDevice(c.deviceId); // also takes this phone off the computer's device list
      } catch {
        setBusy(false);
        setSheet(null);
        return toast("Couldn't remove it from the computer");
      }
      setBusy(false);
    }
    forgetGuestComputer(c.daemonPublicKey);
    store.remove(c.id);
    setSheet(null);
    toast(`${c.label} removed`);
  };

  return (
    <>
      <div className="screen" data-screen="computers">
        <div className="safe-top">
          <div className={"topbar" + (nav.isRoot ? " root" : "")}>
            {!nav.isRoot && <BackButton onClick={nav.back} />}
            <div className="title-col">
              <div className="t">Computers</div>
              <div className="sub">{summaries.length === 0 ? "None paired yet" : `${summaries.length} paired ${summaries.length === 1 ? "computer" : "computers"}${waiting > 0 ? ` · ${waiting} waiting` : ""}`}</div>
            </div>
            {summaries.length > 0 && (
              <button className="icon-btn sq" aria-label="Add computer" onClick={() => nav.push(pairingStart(store.list().length))}>
                <PlusIcon />
              </button>
            )}
          </div>
        </div>
        <div className="scroll">
          <div className="content home-content">
            {summaries.length > 0 ? (
              <>
                <div className="list computer-list">
                  {summaries.map(({ computer: c, state, needsYou, running, wake, wakeSupport }) => {
                    const avail = describeAvailability(state, wake, wakeSupport);
                    const shared = hub.get(c.id)?.getSnapshot().account?.signedIn === true; // the computer is linked to an account
                    const live = (hub.get(c.id)?.getSnapshot().sessions ?? []).filter((x) => x.status === "running");
                    const folders = new Set((hub.get(c.id)?.getSnapshot().sessions ?? []).map((x) => folderKey(x.cwd))).size;
                    const agents = [...new Set(live.map((x) => agentOf(x.command).key))];
                    return (
                      <div className={"computer-card ccard" + (state === "ready" ? "" : " away")} key={c.id}>
                        <button className="computer-main" onClick={() => nav.push({ name: "sessions", computerId: c.id })}>
                          <span className="computer-avatar">
                            <ComputerIcon />
                            <span className="status-dot" style={{ background: STATUS[state].color }} />
                          </span>
                          <span className="row-text">
                            <span className="row-title">{c.label}</span>
                            <span className="row-sub">
                              <span className={"state-pill " + (state === "ready" ? "ok" : state === "offline" || state === "unauthorized" ? "off" : "warn")}>
                                <i aria-hidden />
                                {avail.kind === "waking" || avail.kind === "wake-failed" ? avail.label : STATUS[state].text}
                              </span>
                              {state === "ready" && <span className="run-note">· {running} running</span>}
                            </span>
                          </span>
                          {needsYou > 0 && (
                            <span className="count-pill" aria-label={`${needsYou} waiting`}>
                              {needsYou}
                            </span>
                          )}
                          <ChevronIcon />
                        </button>
                        <button className="comp-line" onClick={() => nav.push({ name: "sessions", computerId: c.id })}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                          </svg>
                          <span className="comp-line-t">Projects</span>
                          <span className="comp-line-v">{folders} {folders === 1 ? "folder" : "folders"}</span>
                          <ChevronIcon />
                        </button>
                        <button className="comp-line" onClick={() => nav.push({ name: "sessions", computerId: c.id })}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="3" y="4" width="18" height="16" rx="3" />
                            <path d="m8 10 3 2-3 2M13 15h3" />
                          </svg>
                          <span className="comp-line-t">Active sessions</span>
                          <span className="comp-agents">
                            {agents.map((k) => (
                              <span className="comp-agent" key={k}>
                                <AgentLogo agent={k} size={15} />
                              </span>
                            ))}
                          </span>
                          <span className="comp-line-v">{live.length}</span>
                          <ChevronIcon />
                        </button>
                        {(avail.kind === "waking" || avail.kind === "wake-failed" || (avail.kind === "offline" && avail.canWake)) && (
                          <div className="ccard-wake" role="status">
                            <span className="ra-hint">{avail.kind === "offline" ? "Offline. It may be asleep." : avail.message}</span>
                            {avail.kind === "waking" ? (
                              <Spinner />
                            ) : (
                              avail.canWake && (
                                <button className="btn secondary" onClick={() => void hub.wake(c.id).catch(() => toast("Couldn't send the wake signal"))}>
                                  {avail.kind === "wake-failed" ? "Try again" : "Wake"}
                                </button>
                              )
                            )}
                          </div>
                        )}
                        <div className="ccard-foot">
                          <span className="ccard-chips">
                            {shared && <span className="cchip-s ok">Shared with your account</span>}
                            <span className="cchip-s mono">{shortFingerprint(c)}</span>
                          </span>
                          <button className="icon-btn" aria-label={`More for ${c.label}`} onClick={() => setSheet({ kind: "menu", id: c.id })}>
                            <MoreIcon />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button className="add-another" onClick={() => nav.push(pairingStart(store.list().length))}>
                  <PlusIcon />
                  Pair another computer
                </button>
              </>
            ) : (
              <div className="welcome">
                <Mascot size={104} front />
                <div className="welcome-name">Brocoded</div>
                <div className="big">Pair your first computer</div>
                <div className="copy">Open the Brocoded app on your computer: its window shows a QR code. Scan it and you’re connected.</div>
                <button className="btn primary" style={{ marginTop: 8 }} onClick={() => nav.push(pairingStart(store.list().length))}>
                  Pair a computer
                </button>
              </div>
            )}
          </div>
        </div>
        {nav.isRoot && <TabBar nav={nav} />}
      </div>

      {sheet && target && (
        <Sheet onClose={() => !busy && setSheet(null)}>
          {sheet.kind === "menu" && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: 0 }}>
                <span style={{ fontSize: 17, fontWeight: 700 }}>{target.label}</span>
                <span className="mono" style={{ fontSize: 13, color: "var(--fg2)" }}>
                  {shortFingerprint(target)}
                </span>
              </div>
              <div className="list">
                <button
                  onClick={() => {
                    setRenameVal(target.label);
                    setSheet({ kind: "rename", id: target.id });
                  }}
                >
                  Rename
                </button>
                <button
                  onClick={() => {
                    setSheet(null);
                    nav.push({ name: "diagnostics", computerId: target.id });
                  }}
                >
                  Connection check
                </button>
                <button
                  style={{ color: "var(--danger)" }}
                  onClick={() => {
                    const st = hub.get(target.id)?.getSnapshot().state;
                    if (st === "unauthorized") return void removeComputer(target); // already removed on the computer
                    setSheet({ kind: st === "ready" ? "remove" : "removeOffline", id: target.id });
                  }}
                >
                  Remove
                </button>
              </div>
              <button className="btn secondary" onClick={() => setSheet(null)}>
                Cancel
              </button>
            </>
          )}
          {sheet.kind === "rename" && (
            <>
              <div style={{ fontSize: 18, fontWeight: 700 }}>Rename computer</div>
              <div className="field">
                <input className="input bg" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} autoFocus aria-label="Computer name" />
                <div className="hint">Only changes the name on this phone.</div>
              </div>
              <div className="pair">
                <button className="btn secondary" onClick={() => setSheet(null)}>
                  Cancel
                </button>
                <button
                  className="btn primary"
                  onClick={() => {
                    store.rename(target.id, renameVal);
                    setSheet(null);
                  }}
                >
                  Save
                </button>
              </div>
            </>
          )}
          {sheet.kind === "remove" && (
            <>
              <div className="q">
                <span className="t">Remove {target.label}?</span>
                <span className="b">This phone will also be removed from the computer's device list. To use it again, you'll need to pair.</span>
              </div>
              <div className="stack">
                <button className="btn danger" disabled={busy} onClick={() => void removeComputer(target)}>
                  {busy && <Spinner />}Remove
                </button>
                <button className="btn secondary" disabled={busy} onClick={() => setSheet(null)}>
                  Cancel
                </button>
              </div>
            </>
          )}
          {sheet.kind === "removeOffline" && (
            <>
              <div className="q">
                <span className="t">{target.label} isn't reachable</span>
                <span className="b">The computer will still list this phone until it is removed there.</span>
              </div>
              <div className="stack">
                <button className="btn danger" onClick={() => void removeComputer(target)}>
                  Remove from this phone only
                </button>
                <button className="btn secondary" onClick={() => setSheet(null)}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </Sheet>
      )}
    </>
  );
}

export { BackButton };
